//This file contains functions for use when rendering DOM elements
//in the recipes section.

import moment from 'moment';
import {sortRecipes, getShownName, getIngredientStatusSummary, 
    getRecipes, getIngredients, toggleIngredient, 
    removeIngredient, getSteps, addStep, removeStep,
    updateStep} from './recipes-functions';
import {getFilters} from './recipes-filters';

//Function to show how long ago a moment in time (given as a
//timestamp) was.
export const showHowLongAgo = (timestamp) => {
    return `Last Edited ${moment(timestamp).fromNow()}.`;
}

//Helper function to render a recipe element in the DOM.
//Input: A single recipe to show.
//Output An <a> tag showing the note that links to the edit page.
const generateRecipeDOM = (recipe) => {
    //Define elements.
    const recipeEl = document.createElement('a');
    const nameEl = document.createElement('p');
    const ingredientStatusEl = document.createElement('p');
    const editStatusEl = document.createElement('p');

    //Set text of name element to whet the user should see.
    nameEl.textContent = getShownName(recipe);

    //Set name formatting.
    nameEl.classList.add('list-item__title');

    //Finish creating and formatting sataus tags.
    ingredientStatusEl.textContent
        = getIngredientStatusSummary(recipe.id);
    ingredientStatusEl.classList.add('list-item__subtitle');

    editStatusEl.textContent 
        = showHowLongAgo(recipe.updatedAt);
    editStatusEl.classList.add('list-item__subtitle');

    //Constuct and format <a> element.
    recipeEl.appendChild(nameEl);
    recipeEl.appendChild(ingredientStatusEl);
    recipeEl.appendChild(editStatusEl);
    recipeEl.classList.add('list-item');

    //Set link of <a> element to edit page.
    recipeEl.setAttribute('href', 
        `./recipes-edit.html#${recipe.id}`);

    //Return <a> tag.
    return recipeEl;
}

//Function to render the list of sorted visible recipes.
export const renderRecipes = () => {
    //Clear old list.
    document.querySelector('#recipes').innerHTML = '';

    //Get the sorted recipe list.
    const filter = getFilters();
    const recipes = sortRecipes(filter.sortBy);

    //Filter the list by the search text and the name the user 
    //sees.
    const filteredList = recipes.filter((recipe) => {
        const shownName = getShownName(recipe);
        return shownName.toLowerCase().includes(
            filter.searchText.toLowerCase());
    });

    //Add new HTML elements.
    if (filteredList.length > 0) {
        //Add list of recipes.
        filteredList.forEach((recipe) => {
            const recipeEl = generateRecipeDOM(recipe);
            document.querySelector('#recipes')
                .appendChild(recipeEl);
        });
    } else {
        //Add message to show there are no recipes.
        const messageEl = document.createElement('h2');
        messageEl.classList.add('empty-message');
        messageEl.textContent = 'You have no recipes saved.';
        document.querySelector('#recipes').appendChild(messageEl);    
    }
}

//Helper function to generate the DOM element for a single 
//ingredient.
//Input: The id of the recipe being shown and a single ingredient.
//Output: The <label> tag that shows the ingredient.
const generateIngredientDom = (recipeID, ingredient) => {
    //Define and get elements.
    const ingredientEl = document.createElement('label');
    const containerEl = document.createElement('div');
    const nameEl = document.createElement('span');
    const buttonEl = document.createElement('button');
    const checkboxEl = document.createElement('input');
    const lastEditedEl = document.querySelector('#last-edited');

    //Setup checkbox.
    checkboxEl.setAttribute('type', 'checkbox');
    if (ingredient.isOnHand) {
        checkboxEl.setAttribute('checked', 'checked');
    }
    checkboxEl.addEventListener('change', () => {
        const recipe = toggleIngredient(recipeID, ingredient.id);

        //If update fails, go to main page.
        if (!recipe) {
            location.assign('./recipes-index.html');
        }

        //Reset web page.
        renderIngredients(recipeID);
    });

    //Setup of name tag.
    nameEl.textContent = ingredient.name;

    //Setup container element.
    containerEl.classList.add('list-item__container');
    containerEl.appendChild(checkboxEl);
    containerEl.appendChild(nameEl);

    //Set up button.
    buttonEl.textContent = 'delete';
    buttonEl.classList.add('button');
    buttonEl.addEventListener('click', () => {
        const recipe = removeIngredient(recipeID, ingredient.id);

        //If update fails, go to main page.
        if (!recipe) {
            location.assign('./recipes-index.html');
        }

        //Reset web page.
        renderIngredients(recipeID);
        lastEditedEl.textContent = 
            showHowLongAgo(recipe.updatedAt);
    });

    //Constuct and return labal element.
    ingredientEl.classList.add('clickable-list-item');
    ingredientEl.appendChild(containerEl);
    ingredientEl.appendChild(buttonEl);
    return ingredientEl;
}

//Function to render the possibly filtered list of ingredients for
//the recipe with the given id.
export const renderIngredients = (recipeID) => {
    //Get the element that will hold the recipe ingredients.
    const parentEl = 
        document.querySelector('#recipe-ingredients');

    //Clear old list.
    parentEl.innerHTML = '';

    //Get unfiltered list of ingredients.
    const ingredients = getIngredients(recipeID);

    //If retrieval fails, go to main page.
    if (!ingredients) {
        location.assign('./recipes-index.html');
    }

    //Get filter field.
    const {hideOnHand} = getFilters();

    const filteredList = ingredients.filter((ingredient) => {
        //Return false if item is on hand and the checkbox to
        //ide it is checked.
        if (ingredient.isOnHand && hideOnHand) {
            return false;
        } else { //show ingredient
            return true;
        }
    });

    //Create and add ingredient summary tag.
    const summaryTag = document.createElement('h2');
    summaryTag.classList.add('user-message');
    summaryTag.textContent = getIngredientStatusSummary(recipeID);
    parentEl.appendChild(summaryTag);

    //Make array to hold tags.
    const tagArray = [];

    //loop through above filtered array.
    filteredList.forEach((ingredient) => {
        const newTag = generateIngredientDom(recipeID, ingredient);

        //Add tag to tag array.
        tagArray.push(newTag);
    });

    //Append tags from above lopp.
    tagArray.forEach((tag) => {
        parentEl.appendChild(tag);
    });
}

//Helper function to generate the DOM elements for a single step. 
//Input: The id of the recipe being shown, the text of a single 
//Step and the number of that step.
//Output: A <div> tag containing the DOM elements needed to show a
//single step.
const generateStepDom = (recipeID, stepText, stepNumber) => {
    //Define and get elements.
    const fullStepEl = document.createElement('div');
    const labelEl = document.createElement('p');
    const stepNumberEl = document.createElement('span');
    const containerEl = document.createElement('div');
    const addPrevEl = document.createElement('button');
    const deleteEl = document.createElement('button');
    const inputEl = document.createElement('textarea');
    const lastEditedEl = document.querySelector('#last-edited');

    //Get index for function calls that require the array index.
    const index = stepNumber - 1;

    //Setup of step number tag.
    stepNumberEl.textContent = `Step #${stepNumber}:`

    //Setup button to insert the previous step.
    addPrevEl.textContent = 'insert step above';
    addPrevEl.classList.add('button');
    addPrevEl.addEventListener('click', () => {
        const recipe = addStep(recipeID, index);

        //If update fails, go to main page.
        if (!recipe) {
            location.assign('./recipes-index.html');
        }

        //Reset web page.
        renderSteps(recipeID);
        lastEditedEl.textContent = 
            showHowLongAgo(recipe.updatedAt);
    });
    
    //Setup delete button.
    deleteEl.textContent = 'delete';
    deleteEl.classList.add('button');
    deleteEl.addEventListener('click', () => {
        const recipe = removeStep(recipeID, index);

        //If update fails, go to main page.
        if (!recipe) {
            location.assign('./recipes-index.html');
        }

        //Reset web page.
        renderSteps(recipeID);
        lastEditedEl.textContent = 
            showHowLongAgo(recipe.updatedAt);
    });

    //Setup container element.
    containerEl.classList.add('list-item__container');
    containerEl.appendChild(addPrevEl);
    containerEl.appendChild(deleteEl);

    //Setup label element.
    labelEl.classList.add('horizontal-list-item');
    labelEl.appendChild(stepNumberEl);
    labelEl.appendChild(containerEl);

    //Setup textarea.
    inputEl.setAttribute('placeholder',
        `Please enter step ${stepNumber}.`);
    inputEl.classList.add('multiple-line-field');
    inputEl.value = stepText;
    inputEl.addEventListener('input', (e) => {
        const recipe = updateStep(recipeID, index, e.target.value);
    
        //If update fails, go to main page.
        if (!recipe) {
            location.assign('./recipes-index.html');
        }
    
        //Reset web page.
        lastEditedEl.textContent = 
            showHowLongAgo(recipe.updatedAt);
    });
    
    //Constuct and return full-step element.
    fullStepEl.appendChild(labelEl);
    fullStepEl.appendChild(inputEl);
    return fullStepEl;
}

//Function to render the list of steps for the recipe with the 
//given id.
export const renderSteps = (recipeID) => {
    //Get the element that will hold the recipe steps.
    const parentEl = document.querySelector('#recipe-steps');

    //Get the "Add New Recipe" button element.
    const buttonEl = document.querySelector('#add-new-step');

    //Clear old list.
    parentEl.innerHTML = '';

    //Get the list of steps.
    const steps = getSteps(recipeID);

    //If retrieval fails, go to main page.
    if (!steps) {
        location.assign('./recipes-index.html');
    }

    //Counter to provide the current step number during each lopp
    //iteration.
    let stepNumber = 1;

    //Loop through above filtered array.
    steps.forEach((step) => {
        //Append tag to parent element.
        parentEl.appendChild(generateStepDom(recipeID, step, 
            stepNumber));

        //prepare for next loop;
        stepNumber++;
    });

    //Check if there are no steps to show.
    if (!steps.length) {
        const messageTag = document.createElement('h2');
        messageTag.classList.add('empty-message');
        messageTag.textContent = 'No steps have been entered for '
            + 'this recipe.'
        parentEl.appendChild(messageTag);
    }

    //Update the "Add New Recipe" button element.
    buttonEl.textContent = !steps.length ? 'Add The First Step' 
        : 'Add The Next Step';
}


//Function to initialize the edit page with the recipe with the
//given ID. This function returns to the main page if the note
//is not found.
export const initEditPage = (recipeID) => {
    //Get correct recipe if it exists.
    const recipes = getRecipes(); 
    const recipe = recipes.find(function (thisRecipe) {
        return thisRecipe.id === recipeID;
    })
    
    //Go to main page if no note is found.
    if (!recipe) {
        location.assign('./recipes-index.html');

        //Function must return to pevent error.
        return;
    }

    //If code gets here, recipe is valid.
    
    //Get page elements.
    const nameEL = document.querySelector('#recipe-name');
    const lastEditedEl = document.querySelector('#last-edited');
    
    //Load recipe data.
    nameEL.value = recipe.name;
    lastEditedEl.textContent = showHowLongAgo(recipe.updatedAt);
    renderIngredients(recipeID);
    renderSteps(recipeID);
}